import type useEmblaCarousel from 'embla-carousel-react';
import type { UseEmblaCarouselType } from 'embla-carousel-react';

// embla-carousel is not a direct dependency — derive the api and plugin types
// through embla-carousel-react's hook signature instead.
type EmblaCarouselType = NonNullable<UseEmblaCarouselType[1]>;
type EmblaPluginType = NonNullable<
    Parameters<typeof useEmblaCarousel>[1]
>[number];

/**
 * The friction embla's engine runs its scroll body at — a private constant in
 * its bundle, so this is a copy. `rail-motion.test.ts` reads the bundle back
 * and holds the copy to it.
 */
export const EMBLA_SCROLL_BODY_FRICTION = 0.68;

/**
 * The share of the distance still to run that embla's scroll body covers in a
 * frame once the motion reaches its steady state, and so the fastest a settle
 * at this `duration` legitimately runs: started from rest the body approaches
 * the share from below and never exceeds it.
 *
 * The body integrates `velocity += distance / duration; velocity *= friction;
 * location += velocity`, a damped spring whose per-frame decay is the larger
 * root of `x² - (friction + 1 - friction / duration)x + friction`. Below about
 * `duration` 22 the roots are imaginary: the spring is underdamped, rings, and
 * has no steady share to hold to. NaN says so, and the caller stands down.
 */
export const maxSettleStepShare = (duration: number): number => {
    const friction = EMBLA_SCROLL_BODY_FRICTION;
    const trace = friction + 1 - friction / duration;
    const discriminant = trace * trace - 4 * friction;

    if (!(discriminant > 0)) return NaN;

    return 1 - (trace + Math.sqrt(discriminant)) / 2;
};

/** Under this the rail has arrived; capping on would only delay `settle`. */
const ARRIVAL_TOLERANCE_PX = 0.5;

interface SettleStep {
    /** Where the rail sat before the tick. */
    from: number;
    /** Where embla's scroll body just put it. */
    to: number;
    /** The snap it is landing on. */
    target: number;
    /** The share of the distance still to run that a tick may cover. */
    maxStepShare: number;
}

/**
 * Where the rail may sit after a tick, held to `maxStepShare` of the distance
 * still to run — which makes the approach monotone, because a step that is
 * never more than a fixed fraction of what is left cannot reach the target,
 * let alone cross it.
 */
export const cappedLocation = ({
    from,
    to,
    target,
    maxStepShare,
}: SettleStep): number => {
    const step = to - from;
    const remaining = target - from;

    if (Math.abs(remaining) < ARRIVAL_TOLERANCE_PX) return to;
    // Moving away from the snap, which is a reversal mid-release rather than an
    // approach to hold back.
    if (Math.sign(step) !== Math.sign(remaining)) return to;

    const limit = Math.abs(remaining) * maxStepShare;

    return Math.abs(step) > limit ? from + Math.sign(step) * limit : to;
};

/**
 * The curve a slide travels on when something other than a hand moves it, fitted
 * to Apple's product rail frame by frame: four consecutive advances measured at
 * 983 / 967 / 1000 / 983ms, all on the same shape, to within 0.28% RMS of
 * `cubic-bezier(0.47, 0.047, 0.52, 0.95)`.
 *
 * It is very nearly the CSS `ease-in-out` keyword, and the point of it is the
 * first half: the slide barely moves for the first fifth of the travel, takes
 * its speed in the middle, and slows again before it lands. A spring cannot do
 * that — its velocity is proportional to the distance still to run, so it is
 * fastest the instant it starts and only ever decays.
 */
const ADVANCE_CURVE = { x1: 0.47, y1: 0.047, x2: 0.52, y2: 0.95 };

/** One axis of a cubic bezier whose first and last control points are 0 and 1. */
const bezierAxis = (a: number, b: number, t: number): number => {
    const inverse = 1 - t;

    return 3 * inverse * inverse * t * a + 3 * inverse * t * t * b + t * t * t;
};

/**
 * The share of the travel that has run after `progress` of the time, along the
 * curve above. Bisection rather than Newton: the curve is monotone in x, thirty
 * halvings put it well inside a pixel, and it runs once a frame.
 */
export const advanceEasing = (progress: number): number => {
    if (progress <= 0) return 0;
    if (progress >= 1) return 1;

    let low = 0;
    let high = 1;
    let t = progress;
    for (let step = 0; step < 30; step++) {
        if (bezierAxis(ADVANCE_CURVE.x1, ADVANCE_CURVE.x2, t) < progress) {
            low = t;
        } else {
            high = t;
        }
        t = (low + high) / 2;
    }

    return bezierAxis(ADVANCE_CURVE.y1, ADVANCE_CURVE.y2, t);
};

export interface RailMotionOptions {
    /**
     * How long a move the reader did not drag takes — a pagination dot, an
     * arrow key, autoplay. Leave unset to keep embla's spring for those.
     */
    advanceMs?: number;
}

/**
 * Owns how the rail moves once nothing is holding it, in two motions that share
 * one `scrollBody.seek` wrapper because they have to agree on which of them owns
 * a tick.
 *
 * **A drag release** carries the speed the hand gave it, so it can only ease
 * out. Embla runs its scroll body from that velocity, and `DragHandler.up()`
 * stiffens the spring in proportion to how much force the snap threw away —
 * `duration` 25 down to 15, `friction` 0.68 up to 0.70 — which is underdamped,
 * and the velocity is not bounded by the one snap the force was clamped to. The
 * invariant here is that the approach stays monotone: each frame covers at most
 * the share of the distance the configured `duration` would, so the rail cannot
 * reach the snap early, let alone cross it.
 *
 * **An advance nothing was holding** starts from rest, so it can ease in as
 * well. With `advanceMs` set, the travel runs `ADVANCE_CURVE` over that many
 * milliseconds rather than embla's spring, which is fastest the instant it
 * starts and only ever decays.
 *
 * Implemented by wrapping `engine.scrollBody.seek`, which the engine calls once
 * per physics tick; embla keeps owning the velocity, direction and previous
 * location, and only the resulting location is written. Re-init runs `init`
 * against the fresh engine, so this survives resizes and option changes.
 *
 * Stands down where there is no snap to land on or its own pace to keep:
 * free-drag carousels and instant jumps. Nothing has to be said about the
 * rail's ends, because `HardEdge` keeps the target inside them.
 */
export function RailMotion({
    advanceMs,
}: RailMotionOptions = {}): EmblaPluginType {
    let stopListening = () => {};

    function init(embla: EmblaCarouselType): void {
        const engine = embla.internalEngine();
        const { dragHandler, scrollBody, location, target } = engine;
        const { dragFree, duration } = engine.options;

        if (dragFree) return;

        const maxStepShare = maxSettleStepShare(duration);
        // An underdamped or otherwise unusable `duration` has no steady rate to
        // hold a release to; leave those to embla rather than invent one.
        const capReleases = maxStepShare > 0 && maxStepShare < 1;
        const easeAdvances =
            typeof advanceMs === 'number' &&
            Number.isFinite(advanceMs) &&
            advanceMs > 0;
        if (!capReleases && !easeAdvances) return;

        // embla emits `pointerUp` once it has picked the snap and stiffened the
        // spring, and `settle` when the rail has arrived. Between those two the
        // motion is a hand's; outside them it is the carousel's own.
        let settling = false;
        const startSettling = () => {
            settling = true;
        };
        const stopSettling = () => {
            settling = false;
        };

        embla.on('pointerDown', stopSettling);
        embla.on('pointerUp', startSettling);
        embla.on('settle', stopSettling);
        stopListening = () => {
            embla.off('pointerDown', stopSettling);
            embla.off('pointerUp', startSettling);
            embla.off('settle', stopSettling);
        };

        // The advance in flight, if any: where it set off from, what it is
        // heading for, and when it started.
        let advance: { from: number; to: number; startedAt: number } | null =
            null;

        const seek = scrollBody.seek.bind(scrollBody);

        scrollBody.seek = () => {
            const from = location.get();
            // A duration of zero is embla setting the location outright rather
            // than stepping toward it: an instant `scrollTo(index, true)`, or a
            // pointer that has just gone down.
            const jumped = !scrollBody.duration();

            seek();

            // A drag in flight is embla's to track, one for one with the hand.
            if (jumped || dragHandler.pointerDown()) {
                advance = null;
                return scrollBody;
            }

            // A release that never moved emits no `settle` to close the window
            // — embla skips the animation when the distance is zero — so the
            // window closes on arrival too.
            if (
                settling &&
                Math.abs(target.get() - from) < ARRIVAL_TOLERANCE_PX
            ) {
                settling = false;
            }

            if (settling) {
                advance = null;
                if (capReleases) {
                    location.set(
                        cappedLocation({
                            from,
                            to: location.get(),
                            target: target.get(),
                            maxStepShare,
                        }),
                    );
                }
                return scrollBody;
            }

            if (!easeAdvances) return scrollBody;

            const destination = target.get();
            if (Math.abs(destination - from) < ARRIVAL_TOLERANCE_PX) {
                advance = null;
                return scrollBody;
            }
            // A fresh advance, or the reader picked a different slide part way
            // through the last one — either way it sets off from here.
            if (!advance || advance.to !== destination) {
                advance = {
                    from,
                    to: destination,
                    startedAt: performance.now(),
                };
            }

            const elapsed = performance.now() - advance.startedAt;
            const progress = Math.min(1, elapsed / (advanceMs as number));
            const travelled = advanceEasing(progress);
            location.set(
                advance.from + (advance.to - advance.from) * travelled,
            );
            if (progress === 1) {
                location.set(advance.to);
                advance = null;
            }

            return scrollBody;
        };
    }

    function destroy(): void {
        // The patched engine is discarded with the embla instance. The
        // listeners are not.
        stopListening();
        stopListening = () => {};
    }

    // Empty, and not `{ advanceMs }`: the plugin type derived through
    // embla-carousel-react only admits embla's own option keys here. The cost
    // is that `arePluginsEqual` cannot see `advanceMs` change, so a carousel
    // that varied it at runtime would keep the first value — every caller
    // passes a constant.
    return { name: 'railMotion', options: {}, init, destroy };
}
